import React, {useEffect, useRef} from 'react';
import {Notification} from '../navigation/page-layout';
import {ServiceResource, useAuth} from '../hooks/use-auth';
import {translateErrorToReactNode} from '../common';
import PortalLoader from '../common/PortalLoader';
import * as QuickSightEmbedding from 'amazon-quicksight-embedding-sdk';
import '../../assets/styles/forecast-styles.scss';
import {useGetTenantConfigurationAsync} from '../hooks/use-configuration-api';
import {AppLayout, ContentLayout, HelpPanel, Link} from '@amzn/awsui-components-react';
import {useGetDashboardUrlByDashboardName} from '../hooks/use-quicksight-api';

export function ViewAccuracyDashboard(props: {pushNotification: (notification: Notification) => void}) {
    const auth = useAuth();

    const forecastStoreInsightClientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(
        ServiceResource.ForecastStoreInsights
    );
    const configurationViewClientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ConfigurationView);

    function createErrorListener<T>(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const {status: getDashboardUrlStatus, value: getDashboardUrlResponse} = useGetDashboardUrlByDashboardName(
        forecastStoreInsightClientConfiguration,
        {
            dashboardName: `Forecast Snapshot WAPE Report ${auth.authInformation!.current!.businessId} ${
                auth.authInformation!.current!.country
            } ${auth.authInformation!.current!.flow}`,
        },
        true,
        createErrorListener('Tenant WAPE Dashboard not Found'),
        [auth]
    );

    const {status: getConfigurationStatus, value: getConfigurationResponse} = useGetTenantConfigurationAsync(
        configurationViewClientConfiguration,
        {
            business: auth.authInformation!.current!.businessId,
            country: auth.authInformation!.current!.country,
            flow: auth.authInformation!.current!.flow,
            groupId: 'forecast',
        },
        true,
        createErrorListener('GetDashboardUrl failed'),
        [auth]
    );

    const isLoading =
        getDashboardUrlStatus === 'idle' ||
        getDashboardUrlStatus === 'pending' ||
        getConfigurationStatus === 'idle' ||
        getConfigurationStatus === 'pending';

    if (isLoading) {
        return <PortalLoader />;
    }

    return (
        <React.Fragment>
            <AppLayout
                disableContentPaddings={true}
                content={
                    <ContentLayout>
                        <ForecastSnapshotAccuracyDashboard
                            url={getDashboardUrlResponse?.dashboardUrl || ''}
                            business={auth.authInformation!.current!.businessId}
                            country={auth.authInformation!.current!.country}
                            flow={auth.authInformation!.current!.flow}
                            confidenceLevel={
                                JSON.parse(getConfigurationResponse?.configuration || '{}')?.forecastGranularityValues
                                    ?.confidenceLevel?.[0] ?? 'NA'
                            }
                            pushNotification={props.pushNotification}
                        />
                    </ContentLayout>
                }
                tools={
                    <HelpPanel header="Accuracy Metrics Explanation">
                        <p>
                            <Link
                                href="https://drive.corp.amazon.com/documents/Groot%20Wiki%20Attachments/Accuracy_Explanation.pdf"
                                external
                                externalIconAriaLabel="Opens in a new tab"
                            >
                                Learn more about Accuracy Metrics (PDF)
                            </Link>
                        </p>
                    </HelpPanel>
                }
                navigationHide
            />
        </React.Fragment>
    );
}

interface ForecastSnapshotAccuracyDashboardProps {
    url: string;
    business: string;
    country: string;
    flow: string;
    confidenceLevel: string;
    pushNotification: (notification: Notification) => void;
}

function ForecastSnapshotAccuracyDashboard(props: ForecastSnapshotAccuracyDashboardProps) {
    const ref = useRef<HTMLDivElement | null>(null);

    function onError(error: any) {
        props.pushNotification({
            type: 'error',
            header: 'Embeddeding Quicksight Accuracy Dashboard failed',
            content: translateErrorToReactNode(error),
        });
    }

    useEffect(() => {
        if (!props.url) {
            return;
        }

        const dashboard = QuickSightEmbedding.embedDashboard({
            url: props.url,
            // ref.current is definitely not null in useEffect
            container: ref.current || '',
            locale: 'en-US',
            footerPaddingEnabled: true,
            parameters: {
                business: props.business,
                country: props.country,
                flow: props.flow,
                confidenceLevel: props.confidenceLevel,
            },
        });
        dashboard.on('error', onError);
    }, [props.business, props.country, props.flow, props.url]);

    if (!props.url) {
        return <div />;
    }

    return <div id={'embeddingContainer'} ref={ref} />;
}
